<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'

    import type { DelegateData, User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import Modal from 'hydria/components/Modal.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const user = sderived(userh.userWritable, ($v) => $v) as Readable<User>
    const ddata = sderived(userh.delegateData, ($v) => $v) as Readable<DelegateData>

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Subaccount {
        id: string
        main_id: string
        p_ecom: boolean
        p_zone: boolean
        p_domain: boolean
        p_domain_ns: boolean

        name: string
        email: string
        main_name: string
        main_email: string
    }

    let isDelegate = $derived($ddata && ($ddata.user_delegate_of.technical.length > 0 || $ddata.user_delegate_of.renewal.length > 0))
    let hasDelegates = $derived($ddata && ($ddata.user_delegates.technical.length > 0 || $ddata.user_delegates.renewal.length > 0))

    let localWorking = $state(false)
    let loaded = $state(false)

    let subAccounts: Subaccount[] = $state([])
    let mainAccount: Subaccount | undefined = $state(undefined)

    function loadResult(data: any) {
        subAccounts = data.subaccounts
        mainAccount = data.main
        localWorking = false
    }
    async function load() {
        let r: SimpleFetchResult

        localWorking = true
        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${$user.id}/subaccount`)
        } catch (e: any) {
            r = e
        }
        localWorking = false
        if (r.success) loadResult(r.data)
        else {
            localWorking = false
            console.error('Load subaccount failed:', r.data)
            notify({
                type: 'danger',
                title: $_('Sous-comptes'),
                text: 'Chargement en erreur' + (r && r.data && r.data.error ? ` : ${r.data.error}` : '.'),
            })
        }
    }

    async function setSubAccount(subId: string, perms: string[]) {
        let r: SimpleFetchResult
        if (localWorking) return

        localWorking = true
        try {
            r = await simpleFetch('POST', `/json/wdom-user/1/${$user.id}/subaccount`, { subaccount_id: subId, permissions: perms })
        } catch (e: any) {
            r = e
        }
        if (r.success) loadResult(r.data)
        else {
            localWorking = false
            console.error('Set subaccount failed:', r.data)
            notify({
                type: 'danger',
                title: $_('Sous-comptes'),
                text: 'Configuration en erreur' + (r && r.data && r.data.error ? ` : ${r.data.error}` : '.'),
            })
        }
    }

    let newIsEdit = $state(false)
    let showAddSubaccount: boolean = $state(false)
    let newSubaccountText = $state('')
    let newSubaccountEcom = $state(false)
    let newSubaccountZone = $state(false)
    let newSubaccountDomainNs = $state(false)

    function openNew() {
        newIsEdit = false
        newSubaccountText = ''
        newSubaccountEcom = false
        newSubaccountZone = false
        newSubaccountDomainNs = false

        showAddSubaccount = true
    }
    function edit(entry: Subaccount) {
        newIsEdit = true
        newSubaccountText = entry.email
        newSubaccountEcom = entry.p_ecom
        newSubaccountZone = entry.p_zone
        newSubaccountDomainNs = entry.p_domain_ns

        showAddSubaccount = true
    }
    async function saveSubaccount() {
        let perms: string[] = []
        if (newSubaccountEcom) perms.push('ecom')
        if (newSubaccountZone) perms.push('zone')
        if (newSubaccountDomainNs && newSubaccountZone) perms.push('domain_ns')
        await setSubAccount(newSubaccountText, perms)

        showAddSubaccount = false
    }

    async function removeSubaccount() {
        await setSubAccount(newSubaccountText, [])

        showAddSubaccount = false
    }

    unsubs.push(
        status.subscribe(($status) => {
            if ($status == 'ready') load()
        }),
    )
</script>

<div class="card">
    <div class="card-title text-primary">{mainAccount ? $_('Sous compte') : $_('Sous comptes')}</div>
    {#if $status == 'loading' || $loading}
        <LoadingPattern pattern={[['flex', ['-:1 0 0', 't:0 0 50px', '-:1 0 0']], 'x', 'x', '-', ['flex', ['-:1 0 0', 't:0 0 50px', '-:1 0 0']], 'x', 'x', '-', '-', ['flex', ['b:1 0 0', 'b:0 0 108px', 'b:0 0 108px']]]} />
    {:else if mainAccount}
        <div class="card-body">
            Ce compte est un sous compte de <a use:sanchor href={spath(`/wdom-user/${mainAccount.main_id}`)}>{mainAccount.main_name}</a>.
        </div>
    {:else if isDelegate}
        <div class="card-body">Ce compte est un délégué.</div>
    {:else}
        <div class="card-body">
            <div class="subaccount-grid">
                <div class="subaccount-grid-row head">
                    <div>Nom</div>
                    <div>Email</div>
                    <div>ecom</div>
                    <div>zone</div>
                </div>
                {#each subAccounts as sub}
                    <div class="subaccount-grid-row">
                        <div><a use:sanchor href={spath(`/wdom-user/${sub['id']}`)}>{sub['name']}</a></div>
                        <div>{sub['email']}</div>
                        <div>
                            {#if sub['p_ecom']}<Icon inline icon="fa:check" />{/if}
                        </div>
                        <div>
                            {#if sub['p_zone']}<Icon inline icon="fa:check" />{/if}
                        </div>
                        <div>
                            <div class="btn-group btn-group-sm">
                                <button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => edit(sub)}><Icon inline icon="mdi:edit" /></button>
                            </div>
                        </div>
                    </div>
                {/each}

                <div class="subaccount-add">
                    <button type="button" class="btn btn-sm btn-outline-secondary" disabled={localWorking} onclick={() => openNew()}>Ajouter</button>
                </div>
            </div>
        </div>
    {/if}
</div>

{#if showAddSubaccount}
    <Modal close={() => (showAddSubaccount = false)} keep={() => saveSubaccount()}>
        <div class="modal-header text-bg-primary">Ajouter un sous compte</div>
        <div class="modal-body">
            <p>ID ou email : <input type="text" bind:value={newSubaccountText} class="form-control" disabled={newIsEdit || localWorking} /></p>
            <div class="checkbox"><label><input type="checkbox" bind:checked={newSubaccountEcom} /> Accès ecom</label></div>
            <div class="checkbox"><label><input type="checkbox" bind:checked={newSubaccountZone} /> Accès édition des zones</label></div>
            <div class="checkbox"><label><input type="checkbox" bind:checked={newSubaccountDomainNs} /> Accès édition des NS et DS sous domaines</label></div>
        </div>
        <div class="modal-footer">
            <button
                type="button"
                class="btn btn-sm btn-danger"
                disabled={localWorking}
                onclick={() => {
                    removeSubaccount()
                }}>Enlever</button
            >
            <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                disabled={localWorking}
                onclick={() => {
                    showAddSubaccount = false
                }}>Fermer</button
            >
            <button
                type="button"
                class="btn btn-sm btn-primary"
                disabled={!newSubaccountText || localWorking}
                onclick={() => {
                    saveSubaccount()
                }}>Sauvegarder</button
            >
        </div>
    </Modal>
{/if}
